import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { cn } from '../lib/utils';
import {
  loadPdfDocument,
  PdfLoadError,
  type PdfDocumentLike,
  type PdfLoadFailure,
  type PdfRenderTaskLike,
  type PdfViewportLike,
} from '../pdf/pdfjs';
import { Button } from './button';
import { Icon } from './icons';

/** PDF points → CSS pixels, so "100%" is the page's real printed size. */
const CSS_UNITS = 96 / 72;
const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
const PAGE_GAP = 16;
const PAD = 16;
/** At most this many pages render at once (large PDFs render progressively). */
const MAX_CONCURRENT_RENDERS = 2;
/** Cap a page's backing canvas (memory) — high zoom on a HiDPI screen lowers
 * the pixel ratio instead of allocating hundreds of MB. */
const MAX_CANVAS_PIXELS = 16_777_216;
/** Viewport height assumed before layout is measured (and in non-layout DOMs). */
const FALLBACK_VIEWPORT_HEIGHT = 800;

const FAILURE_MESSAGES: Record<PdfLoadFailure, string> = {
  encrypted: 'This PDF is password-protected, so it can’t be shown in the viewer.',
  invalid: 'This file is damaged or isn’t a valid PDF, so it can’t be shown in the viewer.',
  failed: 'This PDF couldn’t be displayed.',
};

type Zoom = { mode: 'fit' } | { mode: 'scale'; value: number };

export interface PdfViewerProps {
  /** The PDF bytes. A copy is handed to pdf.js on every open, so the caller's
   * buffer is never detached. */
  data: ArrayBuffer;
  /** Accessible name for the page area (the document name). */
  title: string;
  /** Drawn over the visible page area (e.g. the viewer-identity watermark);
   * it never intercepts the pointer. */
  overlay?: ReactNode;
  /**
   * View-only deterrents: no context menu (Chrome's "Save image as…" on a
   * canvas), no drag-out, no selection. Deterrent-grade only — a screenshot can
   * never be prevented. The canvas-only rendering has no text layer either way.
   */
  protect?: boolean;
  /** Extra line under a load-failure message (e.g. "Use Download…"). */
  errorHint?: string;
  /** Called once the document has opened (page count known) — e.g. to gate an
   * action, such as signing, on the reader actually having the document. */
  onLoad?: (info: { numPages: number }) => void;
  /** Called when the document cannot be shown (encrypted, damaged, failed). */
  onError?: (reason: PdfLoadFailure) => void;
  /** Called with the page in view whenever it changes (and once on open) — the
   * buyer viewer's page-level reading (ADR-0056). */
  onPageChange?: (page: number, numPages: number) => void;
  /**
   * Drawn on top of each page, sized to it (e.g. the redaction review's boxes,
   * in 0..1 page coordinates). Rendered whether or not the page's canvas is
   * drawn yet, so a caller can scroll to an element on any page.
   */
  renderPageOverlay?: (pageNumber: number) => ReactNode;
  /** Test seam — defaults to the real pdf.js loader. */
  loadDocument?: (data: Uint8Array) => Promise<PdfDocumentLike>;
  className?: string;
}

/**
 * In-app PDF viewer (ADR-0027): pages are drawn by pdf.js onto <canvas>, so the
 * browser's own PDF viewer — and its Download/Print toolbar — never appears.
 * Page navigation (buttons, ←/→), zoom (fit width, 100%, +/-), lazy rendering
 * of the pages near the viewport with at most two renders in flight, and a
 * friendly message for encrypted or broken files.
 */
export function PdfViewer({
  data,
  title,
  overlay,
  protect = false,
  errorHint,
  onLoad,
  onError,
  onPageChange,
  renderPageOverlay,
  loadDocument = loadPdfDocument,
  className,
}: PdfViewerProps) {
  const [doc, setDoc] = useState<PdfDocumentLike | null>(null);
  const [failure, setFailure] = useState<PdfLoadFailure | null>(null);
  const [sizes, setSizes] = useState<PdfViewportLike[]>([]);
  const [zoom, setZoom] = useState<Zoom>({ mode: 'fit' });
  const [currentPage, setCurrentPage] = useState(1);
  const [scrollTop, setScrollTop] = useState(0);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadRef = useRef(loadDocument);
  loadRef.current = loadDocument;
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  // Open the document. A fresh copy of the bytes per open: pdf.js may transfer
  // (detach) the buffer to its worker, and StrictMode opens twice.
  useEffect(() => {
    let cancelled = false;
    let opened: PdfDocumentLike | null = null;
    setDoc(null);
    setFailure(null);
    setSizes([]);
    setCurrentPage(1);
    loadRef
      .current(new Uint8Array(data.slice(0)))
      .then(async (pdf) => {
        opened = pdf;
        if (cancelled) return;
        if (pdf.numPages < 1) throw new PdfLoadError('invalid');
        const first = (await pdf.getPage(1)).getViewport({ scale: 1 });
        if (cancelled) return;
        // Every page starts at page 1's size; each page corrects its own size
        // when it renders, so no up-front walk over a large document.
        setSizes(Array.from({ length: pdf.numPages }, () => ({ width: first.width, height: first.height })));
        setDoc(pdf);
        onLoadRef.current?.({ numPages: pdf.numPages });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const reason = err instanceof PdfLoadError ? err.reason : 'failed';
        setFailure(reason);
        onErrorRef.current?.(reason);
      });
    return () => {
      cancelled = true;
      // Cancelled mid-open: release the worker once the document arrives.
      void Promise.resolve().then(() => opened?.destroy());
    };
  }, [data]);

  // Track the scroll viewport's size (fit-width and the render window use it).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setBox({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc]);

  const firstWidth = sizes[0]?.width ?? 0;
  const scale = useMemo(() => {
    if (zoom.mode === 'scale') return zoom.value * CSS_UNITS;
    if (box.width > 0 && firstWidth > 0) return Math.max(0.1, (box.width - 2 * PAD) / firstWidth);
    return CSS_UNITS;
  }, [zoom, box.width, firstWidth]);

  // Page tops follow from the sizes and the scale — no DOM measurement needed.
  const tops = useMemo(() => {
    const out: number[] = [];
    let y = PAD;
    for (const s of sizes) {
      out.push(y);
      y += s.height * scale + PAGE_GAP;
    }
    return out;
  }, [sizes, scale]);

  const viewportHeight = box.height > 0 ? box.height : FALLBACK_VIEWPORT_HEIGHT;
  // Render the pages within half a screen above and a screen below the view.
  const windowStart = scrollTop - viewportHeight / 2;
  const windowEnd = scrollTop + viewportHeight * 2;
  const isNear = (index: number) => {
    const top = tops[index] ?? 0;
    const bottom = top + (sizes[index]?.height ?? 0) * scale;
    return bottom >= windowStart && top <= windowEnd;
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollTop(el.scrollTop);
    // The current page is the last one whose top is above the upper third.
    const probe = el.scrollTop + el.clientHeight / 3;
    let page = 1;
    for (let i = 0; i < tops.length; i += 1) {
      if ((tops[i] ?? 0) <= probe) page = i + 1;
    }
    setCurrentPage(page);
  };

  const numPages = doc?.numPages ?? 0;
  // Tell the caller which page is in view (page-level reading, ADR-0056).
  useEffect(() => {
    if (doc) onPageChangeRef.current?.(currentPage, doc.numPages);
  }, [doc, currentPage]);
  const goToPage = useCallback(
    (page: number) => {
      if (numPages < 1) return;
      const target = Math.min(numPages, Math.max(1, page));
      setCurrentPage(target);
      const el = scrollRef.current;
      const top = (tops[target - 1] ?? PAD) - PAD;
      if (el) el.scrollTop = top;
      setScrollTop(top);
    },
    [numPages, tops],
  );

  const percent = scale / CSS_UNITS;
  const zoomIn = useCallback(() => {
    const next = ZOOM_STEPS.find((z) => z > percent + 0.01);
    if (next) setZoom({ mode: 'scale', value: next });
  }, [percent]);
  const zoomOut = useCallback(() => {
    const prev = [...ZOOM_STEPS].reverse().find((z) => z < percent - 0.01);
    if (prev) setZoom({ mode: 'scale', value: prev });
  }, [percent]);

  // Keep the current page in view when the scale changes.
  const pageRef = useRef(currentPage);
  pageRef.current = currentPage;
  useLayoutEffect(() => {
    if (doc) goToPage(pageRef.current);
    // Only on a scale change — goToPage itself changes with every scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, doc]);

  // Keyboard: ←/→ page, +/- zoom, 0 fit. Ignored while typing in a field or
  // with a modifier held (Ctrl/⌘ +/- stays the browser's own zoom).
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      let handled = true;
      if (e.key === 'ArrowLeft') goToPage(pageRef.current - 1);
      else if (e.key === 'ArrowRight') goToPage(pageRef.current + 1);
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-' || e.key === '_') zoomOut();
      else if (e.key === '0') setZoom({ mode: 'fit' });
      else handled = false;
      if (handled) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [doc, goToPage, zoomIn, zoomOut]);

  const schedule = useMemo(() => createLimiter(MAX_CONCURRENT_RENDERS), [doc]);
  const onPageSize = useCallback((index: number, size: PdfViewportLike) => {
    setSizes((prev) => {
      const old = prev[index];
      if (!old || (Math.abs(old.width - size.width) < 0.5 && Math.abs(old.height - size.height) < 0.5)) {
        return prev;
      }
      const next = prev.slice();
      next[index] = { width: size.width, height: size.height };
      return next;
    });
  }, []);

  const deterrents = protect
    ? {
        onContextMenu: (e: MouseEvent) => e.preventDefault(),
        onDragStart: (e: DragEvent) => e.preventDefault(),
      }
    : {};

  if (failure) {
    return (
      <div className={cn('flex items-center justify-center p-4', className)} role="alert">
        <div className="max-w-md text-center">
          <p className="text-sm text-destructive">{FAILURE_MESSAGES[failure]}</p>
          {errorHint ? <p className="mt-1 text-sm text-muted-foreground">{errorHint}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex min-h-0 flex-col', protect && 'select-none', className)} {...deterrents}>
      <div
        role="toolbar"
        aria-label="PDF controls"
        className="flex flex-wrap items-center justify-between gap-2 border-b bg-background px-3 py-1.5"
      >
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Previous page"
            disabled={!doc || currentPage <= 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            <Icon name="chevronLeft" />
          </Button>
          <span className="min-w-[6.5rem] text-center text-xs tabular-nums text-muted-foreground" aria-live="polite">
            {doc ? `Page ${currentPage} of ${numPages}` : 'Loading…'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Next page"
            disabled={!doc || currentPage >= numPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            <Icon name="chevronRight" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" aria-label="Zoom out" disabled={!doc} onClick={zoomOut}>
            <Icon name="minus" />
          </Button>
          <span className="w-12 text-center text-xs tabular-nums text-muted-foreground" data-testid="pdf-zoom">
            {Math.round(percent * 100)}%
          </span>
          <Button variant="ghost" size="sm" aria-label="Zoom in" disabled={!doc} onClick={zoomIn}>
            <Icon name="plus" />
          </Button>
          <Button
            variant={zoom.mode === 'fit' ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={zoom.mode === 'fit'}
            disabled={!doc}
            onClick={() => setZoom({ mode: 'fit' })}
          >
            Fit width
          </Button>
          <Button
            variant={zoom.mode === 'scale' && zoom.value === 1 ? 'secondary' : 'ghost'}
            size="sm"
            aria-pressed={zoom.mode === 'scale' && zoom.value === 1}
            disabled={!doc}
            onClick={() => setZoom({ mode: 'scale', value: 1 })}
          >
            100%
          </Button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="absolute inset-0 overflow-auto bg-muted/30"
          role="document"
          aria-label={title}
          tabIndex={0}
        >
          {doc ? (
            // w-max + min-w-full + mx-auto pages: centred when narrower than
            // the view, scrollable from the left edge when zoomed wider (a
            // centring flexbox would push the left overflow out of reach).
            <div className="flex w-max min-w-full flex-col" style={{ padding: PAD, gap: PAGE_GAP }}>
              {sizes.map((size, i) => (
                <PdfPageView
                  key={i}
                  doc={doc}
                  index={i}
                  size={size}
                  scale={scale}
                  near={isNear(i) || i + 1 === currentPage}
                  schedule={schedule}
                  onSize={onPageSize}
                  overlay={renderPageOverlay?.(i + 1)}
                />
              ))}
            </div>
          ) : (
            <p className="p-8 text-center text-sm text-muted-foreground">Loading document…</p>
          )}
        </div>
        {overlay ? <div className="pointer-events-none absolute inset-0">{overlay}</div> : null}
      </div>
    </div>
  );
}

interface PdfPageViewProps {
  doc: PdfDocumentLike;
  index: number;
  size: PdfViewportLike;
  scale: number;
  near: boolean;
  schedule: Limiter;
  onSize: (index: number, size: PdfViewportLike) => void;
  overlay?: ReactNode;
}

/** One page: a sized placeholder that draws onto its canvas only while near the
 * viewport, and releases the canvas memory when scrolled far away. */
function PdfPageView({ doc, index, size, scale, near, schedule, onSize, overlay }: PdfPageViewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<'idle' | 'rendered' | 'error'>('idle');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!near) {
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
      setState('idle');
      return;
    }
    const controller = new AbortController();
    let task: PdfRenderTaskLike | null = null;
    schedule(async () => {
      const page = await doc.getPage(index + 1);
      if (controller.signal.aborted || !canvasRef.current) return false;
      onSize(index, page.getViewport({ scale: 1 }));
      const viewport = page.getViewport({ scale });
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      const ratio = Math.min(dpr, Math.sqrt(MAX_CANVAS_PIXELS / Math.max(1, viewport.width * viewport.height)));
      const target = canvasRef.current;
      target.width = Math.max(1, Math.floor(viewport.width * ratio));
      target.height = Math.max(1, Math.floor(viewport.height * ratio));
      task = page.render({
        canvas: target,
        viewport,
        transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
      });
      // pdf.js rejects a cancelled render; racing the signal also frees the
      // render slot if a cancel ever leaves the promise pending.
      await Promise.race([
        task.promise,
        new Promise((_, reject) =>
          controller.signal.addEventListener('abort', () => reject(new Error('aborted')), { once: true }),
        ),
      ]);
      return true;
    }, controller.signal)
      .then((done) => {
        if (done && !controller.signal.aborted) setState('rendered');
      })
      .catch(() => {
        // A cancelled render (zoom change, scrolled away, closed) is expected.
        if (!controller.signal.aborted) setState('error');
      });
    return () => {
      controller.abort();
      task?.cancel();
    };
  }, [doc, index, scale, near, schedule, onSize]);

  const width = Math.round(size.width * scale);
  const height = Math.round(size.height * scale);
  return (
    <div
      className="relative mx-auto shrink-0 bg-white shadow-sm ring-1 ring-black/5"
      style={{ width, height }}
      data-page={index + 1}
      aria-label={`Page ${index + 1}`}
      role="img"
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" draggable={false} />
      {state === 'error' ? (
        <p className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-destructive">
          This page couldn’t be displayed.
        </p>
      ) : state === 'idle' ? (
        <p className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
          Page {index + 1}
        </p>
      ) : null}
      {overlay}
    </div>
  );
}

type Limiter = <T>(job: () => Promise<T>, signal: AbortSignal) => Promise<T>;

/** A tiny FIFO concurrency limiter: at most `max` jobs run at once; a job whose
 * signal aborts before it starts is dropped without running. */
function createLimiter(max: number): Limiter {
  let active = 0;
  const queue: Array<() => void> = [];
  const pump = () => {
    while (active < max && queue.length > 0) {
      const start = queue.shift();
      if (start) {
        active += 1;
        start();
      }
    }
  };
  return <T,>(job: () => Promise<T>, signal: AbortSignal) =>
    new Promise<T>((resolve, reject) => {
      const start = () => {
        const finish = () => {
          active -= 1;
          pump();
        };
        if (signal.aborted) {
          finish();
          reject(new Error('aborted'));
          return;
        }
        job().then(resolve, reject).finally(finish);
      };
      queue.push(start);
      signal.addEventListener(
        'abort',
        () => {
          const at = queue.indexOf(start);
          if (at !== -1) {
            queue.splice(at, 1);
            reject(new Error('aborted'));
          }
        },
        { once: true },
      );
      pump();
    });
}
